import { useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { ManufacturingModelsDictateMicButton } from "@/components/manufacturing/ManufacturingModelsDictateMicButton";
import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { importQcChecklistCsv, readQcChecklistPhoto } from "@/lib/manufacturing/builderAssistantChecklistClient";
import { formatQcChecklistCsv, parseQcChecklistSpeech } from "@/lib/manufacturing/qcChecklistImport";

export function BuilderAssistantChecklistImport(props: {
  onImported: () => Promise<void> | void;
  existingCategories?: readonly string[];
}) {
  const [csv, setCsv] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function appendRows(text: string) {
    const rows = parseQcChecklistSpeech(text, props.existingCategories ?? []);
    if (rows.length === 0) {
      setError("Say category 1, new category, or next category. For a line, say item 1, next item, or add it to a category.");
      return false;
    }
    const block = formatQcChecklistCsv(rows);
    setCsv((current) => (current.trim() ? `${current.trim()}\n${block}` : block));
    setError(null);
    return true;
  }

  async function onPhoto(file: File) {
    setBusy(true);
    setError(null);
    const res = await readQcChecklistPhoto(file);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setCsv((current) => (current.trim() ? `${current.trim()}\n${res.csv.trim()}` : res.csv.trim()));
  }

  return (
    <form
      className="space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        void importQcChecklistCsv(csv).then(async (res) => {
          setBusy(false);
          if (!res.ok) {
            setError(res.error);
            return;
          }
          setCsv("");
          await props.onImported();
        });
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Import rows</span>
        <InlineHelp glyph="!" label="How to import checklist rows">
          One line is one item. Three columns are category, item, description. Two columns are an item
          and a description under General. Categories are saved in capitals. Item text keeps the case you type.
          In one dictation, mark each category with category 1, new category, or next category, then mark
          each line with item 1, next item, or add it to a category. Or write Category FLOOR, then each
          check separated by a comma. An item named for a category already
          on the checklist is added there. A category that is not on the checklist is created. A photo
          fills the same box. Import rows saves it.
        </InlineHelp>
      </div>
      <ManufacturingModelsDictateMicButton
        extractLabel="Add to import"
        placeholder="category 1, next category, then add each item to a category"
        clearOnExtract
        disabled={busy}
        onComplete={appendRows}
      />
      <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
        Read a photo
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void onPhoto(file);
          }}
        />
      </label>
      <textarea
        className={`${inputClass} min-h-24 w-full`}
        value={csv}
        placeholder={"category, item, description"}
        onChange={(event) => setCsv(event.target.value)}
      />
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      <button type="submit" className={ghostBtnClass} disabled={busy || !csv.trim()}>
        Import rows
      </button>
    </form>
  );
}
